import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import HsgAdminBookingCard from './HsgAdminBookingCard';
import ManagerTrainingResources from './ManagerTrainingResources';
import { toast } from 'sonner';
import { CalendarClock, ChevronDown, ClipboardCheck, Download, ExternalLink, GraduationCap, RefreshCw, ShieldCheck, Upload } from 'lucide-react';

/**
 * Lightweight entry point for the shared Training & Audit Hub.
 * The full evidence matrix stays in its existing tab. This card surfaces one
 * action, one-pass certificates and safe manager support requests.
 */
function TrainingHubCore({ employeeId, mode = 'manager', isAuditor = false, token, workspaceId = '', onEvidenceUpdated }) {
  const worker = mode === 'worker';
  const readOnly = !worker && isAuditor;
  const bearer = worker ? localStorage.getItem('workerToken') : token;
  const headers = { Authorization: `Bearer ${bearer}` };
  const [hub, setHub] = useState(null);
  const [queue, setQueue] = useState(null);
  const [queueError, setQueueError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showManagerDetails, setShowManagerDetails] = useState(false);
  const [appointment, setAppointment] = useState({});
  const [venue, setVenue] = useState({});
  const [providerNames, setProviderNames] = useState({});
  const [bookingReferences, setBookingReferences] = useState({});
  const [scopeSelections, setScopeSelections] = useState({});
  const [showScopeReview, setShowScopeReview] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const apiPath = worker
    ? `${API_BASE}/worker/training/hub`
    : `${API_BASE}/employees/${employeeId}/training/hub`;

  const refresh = useCallback(async () => {
    if (!bearer || (!worker && !employeeId)) return;
    try {
      setLoading(true);
      const data = await axios.get(apiPath, { headers });
      setHub(data.data);
      setError('');
      if (!worker && !readOnly) {
        try {
          const queueResult = await axios.get(`${API_BASE}/training/hub/review-queue`, { headers });
          setQueue(queueResult.data);
          setQueueError('');
        } catch (queueErr) {
          // Preserve the person's loaded training data but never imply there
          // are no pending reviews when the separate queue cannot be read.
          setQueue(null);
          setQueueError('Training review queue unavailable. Check again before completing reviews.');
        }
      }
    } catch (err) {
      setHub(null); // A failed primary read cannot leave stale training shown as current.
      setQueue(null);
      setError(err.response?.data?.detail || 'Training hub is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  // bearer is the current token snapshot; request headers are created for that token
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiPath, bearer, worker, readOnly, employeeId]);

  useEffect(() => {
    // A different applicant must not inherit open review tools or cached scope
    // choices from the previous person's certificate.
    setShowScopeReview(null);
    setScopeSelections({});
    refresh();
  }, [refresh]);

  const requestSupport = async (action) => {
    if (!action || mutating) return;
    setMutating(true);
    try {
      const kind = action.type === 'request_competency' ? 'competency' : 'training';
      const route = worker
        ? `${API_BASE}/worker/training/hub/support-requests`
        : `${API_BASE}/employees/${employeeId}/training/hub/support-requests`;
      const res = await axios.post(route, {
        kind, training_code: action.id.replace(/^[^:]+:/, '')
      }, { headers });
      toast.success(res.data.created ? 'Training request sent to your organisation' : 'Your organisation already has this request');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the request');
    } finally { setMutating(false); }
  };

  const requestAllMissingTraining = async () => {
    if (!worker || mutating || !hub) return;
    const currentRequests = new Set((hub.support_requests || [])
      .filter(req => ['requested', 'acknowledged', 'arranged', 'enrolled'].includes(req.status) && req.kind === 'training')
      .map(req => req.training_code));
    const available = Array.isArray(hub.actions) && hub.actions.length ? hub.actions : [hub.next_action];
    // A single click asks the employer for all outstanding courses, not just
    // the first course in the prioritised next-action list.
    const missing = Array.from(new Map(available
      .filter(a => a?.type === 'arrange_training' && a.id?.startsWith('training:'))
      .map(a => [a.id.slice('training:'.length), a])).values())
      .filter(a => !currentRequests.has(a.id.slice('training:'.length)));
    if (!missing.length) {
      toast.success('Your organisation already has your training requests');
      return;
    }
    setMutating(true);
    try {
      const result = await Promise.allSettled(missing.map(item => axios.post(
        `${API_BASE}/worker/training/hub/support-requests`,
        { kind: 'training', training_code: item.id.slice('training:'.length) },
        { headers },
      )));
      const succeeded = result.filter(item => item.status === 'fulfilled').length;
      if (succeeded) toast.success(`${succeeded} training request${succeeded === 1 ? '' : 's'} sent to your organisation`);
      if (succeeded < result.length) toast.error('Some requests could not be sent. Please try again.');
      await refresh();
    } catch (err) {
      toast.error('Could not send training requests. Please try again.');
    } finally { setMutating(false); }
  };

  const updateRequest = async (req, status) => {
    if (mutating) return;
    setMutating(true);
    try {
      const at = appointment[req.id];
      const location = venue[req.id];
      const payload = status === 'arranged'
        ? { status, appointment_at: at ? new Date(at).toISOString() : null, location }
        : status === 'enrolled'
          ? { status, provider_name: providerNames[req.id]?.trim(), booking_reference: bookingReferences[req.id]?.trim() }
          : { status };
      await axios.patch(`${API_BASE}/training/hub/support-requests/${req.id}`, payload, { headers });
      toast.success(status === 'enrolled' ? 'Enrolment recorded. Certificate still needed.' : status === 'arranged' ? 'Appointment recorded — attendance still needs verification' : 'Request updated');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update request');
    } finally { setMutating(false); }
  };

  const viewSourceCertificate = async (cert) => {
    if (!cert.view_route) return;
    // Reserve the new tab directly from the click, before awaiting the
    // authenticated request, so browsers do not suppress the evidence viewer.
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      toast.error('Please allow a pop-up to view the certificate');
      return;
    }
    try {
      tab.opener = null;
      const response = await axios.get(`${API_BASE}${cert.view_route}`,
        { headers, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      tab.location.replace(blobUrl);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
    } catch (err) {
      tab.close();
      toast.error('Could not open the original certificate');
    }
  };

  const confirmSafeguardingScope = async (course) => {
    const scope = scopeSelections[course.record_id];
    if (!scope || !employeeId || !course.record_id || mutating) return;
    const scopeDescription = scope === 'combined' ? 'both Adults and Children' : scope;
    if (!window.confirm(`Have you opened the source certificate and confirmed that it explicitly covers ${scopeDescription} safeguarding? This will update induction evidence but will not award the Care Certificate.`)) return;
    setMutating(true);
    try {
      const evidence_statement = `I personally reviewed the source certificate and confirmed that it explicitly lists ${scopeDescription} safeguarding training.`;
      await axios.patch(`${API_BASE}/employees/${employeeId}/training/hub/safeguarding/${course.record_id}/scope`,
        { scope, evidence_statement }, { headers });
      toast.success('Safeguarding subject review saved.');
      await refresh();
      // The training matrix is a sibling of the hub in employee profiles.
      // An induction/parent refresh alone leaves its old "scope review needed"
      // status visible until an unrelated timer/focus refresh occurs.
      window.dispatchEvent(new CustomEvent('careops:safeguarding-scope-reviewed', {
        detail: { employeeId, workspaceId, recordId: course.record_id },
      }));
      if (onEvidenceUpdated) await onEvidenceUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update safeguarding scope');
    } finally { setMutating(false); }
  };

  const downloadAudit = async () => {
    try {
      const response = await axios.get(`${apiPath}/audit.csv`, { headers, responseType: 'blob' });
      const href = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = `training-audit-${employeeId}.csv`;
      anchor.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error('Could not export training evidence');
    }
  };

  if (loading && !hub) return <div className="rounded-xl border p-4 text-sm text-slate-500">Loading your training next steps…</div>;
  if (error && !hub) return <div className="rounded-xl border border-amber-200 p-4 text-sm text-amber-800" role="alert">{error}<button type="button" onClick={refresh} className="ml-3 underline">Retry</button></div>;
  if (!hub) return null;

  const action = hub.next_action;
  const staffNext = worker ? hub.training_next_step : null;
  const staffWaiting = staffNext && ['checking', 'booked', 'requested'].includes(staffNext.phase);
  const certificateCount = hub.summary?.source_certificates || 0;
  const openRequests = (hub.support_requests || []).filter(req => ['requested', 'acknowledged', 'arranged', 'enrolled'].includes(req.status));
  const requestedTrainingCode = action?.id?.startsWith('training:') ? action.id.slice('training:'.length) : '';
  const activeTrainingRequest = openRequests.find(req => req.kind === 'training' && req.training_code === requestedTrainingCode);
  const remainingCourses = (Array.isArray(hub.actions) && hub.actions.length ? hub.actions : [action])
    .filter(item => item?.type === 'arrange_training' && item.id?.startsWith('training:'))
    .filter(item => !openRequests.some(req => req.kind === 'training' && req.training_code === item.id.slice('training:'.length))).length;
  // One visible home for safeguarding scope review. The passport below stays
  // an evidence/history record, not a second place to change the same truth.
  const safeguardingReviews = (hub.certificates || []).flatMap(cert =>
    (cert.courses || [])
      .filter(course => course.scope_review_available && course.record_id)
      .map(course => ({ cert, course }))
  );
  const needsSafeguardingReview = safeguardingReviews.some(({ course }) => course.needs_scope_review);
  const scopeReviewVisible = showScopeReview === null ? needsSafeguardingReview : showScopeReview;
  const scopeNames = {
    adults: 'Adults only', children: 'Children only',
    combined: 'Adults and Children', generic: 'Not specified',
  };
  const employeeQueueRequests = (queue?.support_requests || []).filter(req => req.employee_id === employeeId);
  // A person's staff file should not display the tenant-wide manager queue
  // as though all those tasks belong to this worker.
  const queueCount = [
    ...(queue?.pending_evidence_reviews || []),
    ...employeeQueueRequests,
  ].filter(item => item.employee_id === employeeId).length;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4" data-testid="training-audit-hub">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal-700" /><h3 className="font-semibold text-slate-900">{worker ? 'Your training' : 'Training next steps'}</h3></div>
          {!worker && <p className="text-xs text-slate-500 mt-1">See the next step below. Verified certificates and previous decisions stay on file.</p>}
        </div>
        <div className="flex gap-2">
          {!worker && (readOnly || showManagerDetails) && (
            <button type="button" onClick={downloadAudit} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-medium">
              <Download className="h-4 w-4" /> Audit CSV
            </button>
          )}
          <button type="button" onClick={refresh} className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-medium">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {!worker && !readOnly && (queueError || queueCount > 0 || (hub.unmapped_evidence || []).length > 0) && (
        <div role={queueError ? 'alert' : 'status'}
          className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="manager-training-pending-review-notice">
          <span>{queueError || `${queueCount} training review${queueCount === 1 ? '' : 's'} pending for this person`}
            {!queueError && (hub.unmapped_evidence || []).length > 0
              ? ` · ${hub.unmapped_evidence.length} course${hub.unmapped_evidence.length === 1 ? '' : 's'} to identify`
              : null}</span>
          <button type="button" onClick={() => setShowManagerDetails(true)}
            className="min-h-10 rounded-lg border border-amber-300 bg-white px-3 py-2 font-semibold">
            Review details
          </button>
        </div>
      )}

      {!worker && (readOnly || showManagerDetails) && <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 text-sm">
        {[
          ['Certificates', certificateCount],
          ['Verified courses', hub.summary?.verified_training_records || 0],
          ['Awaiting review', hub.summary?.pending_certificate_reviews || 0],
          ['Support requests', hub.summary?.active_support_requests || 0],
        ].map(([label, value]) => (
          <div key={label} className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-600">{label}</p><p className="text-lg font-semibold">{value}</p></div>
        ))}
      </div>}

      {/* Booking and full provider catalogue belong in the manager's
          detail workspace, not the first screen a recruiter opens. */}
      {!worker && !readOnly && showManagerDetails && (
        <HsgAdminBookingCard
          employeeId={employeeId}
          token={bearer}
          requests={hub.support_requests || []}
          onRecorded={refresh}
        />
      )}
      {!worker && !readOnly && showManagerDetails && (
        <ManagerTrainingResources employeeId={employeeId} token={bearer} hub={hub} />
      )}

      {!worker && hub.safeguarding_induction_mismatch && (
        <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="safeguarding-induction-mismatch">
          <p className="font-semibold">Check safeguarding coverage</p>
          <p className="mt-1">Confirm which safeguarding subjects this certificate covers before signing off induction.</p>
          {(readOnly || showManagerDetails) && (
            <div data-testid="safeguarding-review-details">
              <p className="mt-2">{hub.safeguarding_induction_mismatch.message}</p>
              {(hub.safeguarding_induction_mismatch.candidate_evidence || []).map((candidate, index) => (
            <p key={candidate.record_id || index} className="mt-1 text-xs">
              {candidate.completion_date_recorded ? 'Completion date recorded' : 'Completion date missing'}
              {' · '}{candidate.induction_current ? 'Current for induction' : 'Not current for induction'}
              {' · '}{candidate.source_certificate_linked ? 'Source certificate linked' : 'Source certificate not linked'}
            </p>
          ))}
          {hub.safeguarding_induction_mismatch.reason === 'approved_evidence_not_current_for_induction' && (
            <p className="mt-1 text-xs">A manager must check the actual course completion and expiry dates in the approved record. Do not change the date to the approval date.</p>
          )}
          <p className="mt-1 text-xs">
            {hub.safeguarding_induction_mismatch.reason === 'scope_review_required'
              ? 'A general Safeguarding approval is not proof of Adults or Children coverage. Check the original certificate and confirm the exact subject. Do not approve the same evidence again.'
              : hub.safeguarding_induction_mismatch.reason === 'induction_read_path_mismatch'
                ? 'Matching verified evidence exists but the induction view disagrees. Reconcile this before sign-off; the original approval is retained.'
                : 'Check actual certificate dates and coverage. Do not make a record current by changing its date to the approval date.'}
          </p>
            </div>
          )}
          {!readOnly && (
            <button type="button" className="mt-3 rounded-lg bg-amber-900 px-3 py-2 text-sm font-semibold text-white"
              data-testid="review-safeguarding-scope"
              onClick={() => { setShowManagerDetails(true); setShowScopeReview(true); }}>
              Review certificate
            </button>
          )}
        </div>
      )}


      {/* Primary manager safeguarding task: no nested advanced settings/passport hunt. */}
      {!worker && safeguardingReviews.length > 0 && (
        <section
          className={`rounded-xl border p-3 sm:p-4 ${needsSafeguardingReview ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}
          data-testid="safeguarding-coverage-review"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-slate-900">
                Safeguarding certificate — Adults / Children
              </h4>
              <p className="mt-1 text-sm text-slate-700">
                {needsSafeguardingReview
                  ? 'Subject coverage needs review. Open the original certificate and record whether it covers Adults, Children or Both.'
                  : 'Coverage is recorded. You can review it again if the original certificate names additional subjects.'}
              </p>
              <p className="mt-1 text-xs text-slate-600">
                This reviews certificate coverage only. Care Certificate assessment and sign-off remain separate.
              </p>
            </div>
            <button type="button"
              onClick={() => setShowScopeReview(!scopeReviewVisible)}
              aria-expanded={scopeReviewVisible}
              data-testid="open-safeguarding-coverage"
              className="min-h-11 shrink-0 rounded-lg border border-teal-700 bg-white px-3 py-2 text-sm font-semibold text-teal-900">
              {scopeReviewVisible ? 'Hide coverage details' : 'Review Adults / Children'}
            </button>
          </div>
          {scopeReviewVisible && (
            <div className="mt-3 space-y-3" data-testid="safeguarding-scope-controls">
              {safeguardingReviews.map(({ cert, course }) => (
                <div key={course.record_id} className="rounded-lg border border-slate-200 bg-white p-3">
                  <p className="font-semibold text-sm text-slate-900">{course.title || 'Safeguarding training'}</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Source: {cert.file_name || 'Original certificate'}
                    {' · '}Current scope: {scopeNames[course.safeguarding_scope] || 'Not specified'}
                  </p>
                  {cert.view_route ? (
                    <button type="button"
                      onClick={() => viewSourceCertificate(cert)}
                      className="mt-2 inline-flex min-h-10 items-center rounded-lg border border-teal-700 bg-white px-3 text-sm font-semibold text-teal-900"
                      data-testid={`safeguarding-source-${course.record_id}`}>
                      <ExternalLink className="mr-2 h-4 w-4" /> Open original certificate
                    </button>
                  ) : (
                    <p className="mt-2 text-xs text-amber-800">Original certificate is unavailable. Do not change coverage without evidence.</p>
                  )}
                  {!readOnly && cert.view_route && (
                    <div className="mt-3 flex flex-wrap items-end gap-2">
                      <label className="flex min-w-[180px] flex-1 flex-col gap-1 text-xs font-medium text-slate-700">
                        Subjects verified on the original certificate
                        <select
                          aria-label={`Safeguarding scope for ${course.title}`}
                          value={['adults', 'children', 'combined'].includes(scopeSelections[course.record_id] ?? course.safeguarding_scope) ? (scopeSelections[course.record_id] ?? course.safeguarding_scope) : ''}
                          onChange={e => setScopeSelections(old => ({ ...old, [course.record_id]: e.target.value }))}
                          className="min-h-11 rounded-lg border border-slate-300 bg-white p-2 text-sm"
                        >
                          <option value="">Select verified coverage</option>
                          <option value="adults">Adults only</option>
                          <option value="children">Children only</option>
                          <option value="combined">Adults and Children (both)</option>
                        </select>
                      </label>
                      <button type="button"
                        disabled={mutating || !scopeSelections[course.record_id] || scopeSelections[course.record_id] === course.safeguarding_scope}
                        onClick={() => confirmSafeguardingScope(course)}
                        className="min-h-11 rounded-lg bg-teal-800 px-3 text-sm font-semibold text-white disabled:opacity-50"
                        data-testid={`confirm-safeguarding-${course.record_id}`}>
                        Confirm reviewed scope
                      </button>
                    </div>
                  )}
                  {readOnly && <p className="mt-2 text-xs text-slate-600">Read-only audit view. Only an authorised manager may correct coverage.</p>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {!worker && !readOnly && (
        <button type="button" onClick={() => setShowManagerDetails(value => !value)}
          aria-expanded={showManagerDetails}
          data-testid="manager-training-advanced-toggle"
          className="min-h-11 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-800">
          {showManagerDetails ? 'Hide detailed training checks' : 'View detailed training checks'}
        </button>
      )}
      {staffWaiting && (
        <div role="status" className="rounded-xl bg-teal-50 px-4 py-3 text-sm text-teal-900"
          data-testid="worker-training-current-status">
          <p className="font-semibold">{staffNext.title}</p>
          <p className="mt-1 text-xs">{staffNext.description}</p>
        </div>
      )}
      {/* Everyone sees their next step. Provider administration, passports and
          historical audit details only open when explicitly requested. */}
      {action ? (
        <div className="rounded-xl border border-teal-100 bg-teal-50/40 p-4" data-testid="training-next-action">
          <p className="text-xs uppercase tracking-wide font-semibold text-teal-800">Next step</p>
          <p className="font-semibold text-slate-900 mt-1">{action.title}</p>
          <p className="text-sm text-slate-700 mt-1">{action.worker_instruction}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {worker && action.type === 'arrange_training' ? (
              <div className="w-full space-y-2" data-testid="worker-training-choices">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <a href="/worker/dashboard?step=mandatory_training"
                    className="min-h-11 inline-flex items-center justify-center gap-2 rounded-lg border border-teal-700 bg-white px-3 py-3 text-sm font-semibold text-teal-800"
                    data-testid="open-worker-training-checklist">
                    <Upload className="h-4 w-4" /> Open my training checklist
                  </a>
                  {!staffWaiting && (
                    <button type="button" disabled={mutating || remainingCourses === 0}
                      onClick={requestAllMissingTraining}
                      className="min-h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-3 text-sm font-semibold text-white disabled:opacity-50">
                      <CalendarClock className="h-4 w-4" /> {remainingCourses === 0 ? 'Request received' : 'I need training'}
                    </button>
                  )}
                </div>
                {activeTrainingRequest && <p role="status" className="text-xs text-slate-700">
                  {activeTrainingRequest.status === 'enrolled'
                    ? `Your organisation recorded your enrolment with ${activeTrainingRequest.provider_name || 'the provider'}. Complete the course, then upload your certificate.`
                    : 'Your organisation has your training request and will arrange the next step.'}
                </p>}
                <p className="text-xs text-slate-600">Your organisation arranges required workplace training. You will not be asked to pay here.</p>
              </div>
            ) : (
              <>
                {!worker && action.resource?.url && (
                  <a href={action.resource.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 bg-teal-700 text-white rounded-lg px-3 py-2 text-sm font-medium">
                    <GraduationCap className="h-4 w-4" /> Official learning <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {!readOnly && ['arrange_training', 'request_competency'].includes(action.type) && (
                  <button disabled={mutating} type="button" onClick={() => requestSupport(action)} className="rounded-lg bg-slate-900 text-white px-3 py-2 text-sm font-medium disabled:opacity-60">
                    <CalendarClock className="h-4 w-4 inline mr-1" /> Request training / assessment
                  </button>
                )}
              </>
            )}
            {worker && action.route && <a href={action.route} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium">Continue induction</a>}
          </div>
          {!worker && action.resource && <p className="text-xs mt-2 text-slate-500">{action.resource.note}</p>}
          {!worker && action.resource?.steps && (
            <ol className="mt-2 text-xs text-slate-700 space-y-1 list-decimal pl-5">
              {action.resource.steps.map(step => <li key={step.title}>{step.title}{step.status === 'manager_arranged' ? ' — Your organisation arranges this' : ''}</li>)}
            </ol>
          )}
        </div>
      ) : (
        <p className="text-sm text-teal-800 bg-teal-50 rounded-lg p-3">No urgent training action is currently identified. This does not replace role or practical competency checks.</p>
      )}

      {(worker || readOnly || showManagerDetails) && (
        <>
      {openRequests.length > 0 && (!worker || action?.type !== 'arrange_training') && (
        <div className="text-xs text-slate-700 rounded-lg border bg-slate-50 p-3">
          <p className="font-semibold mb-1">Your support requests</p>
          {openRequests.slice(0, 4).map(req => <p key={req.id}>{req.training_code?.replace(/_/g, ' ')} — {req.status}{req.appointment_at ? ` · ${new Date(req.appointment_at).toLocaleString('en-GB')}` : ''}</p>)}
        </div>
      )}

      <button type="button" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <ClipboardCheck className="h-4 w-4" /> {expanded ? 'Hide' : 'View'} training passport ({certificateCount} certificates) <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="space-y-3" data-testid="training-passport">
          {(hub.certificates || []).map(cert => (
            <div key={cert.document_id} className="border rounded-xl p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium text-sm">{cert.file_name}</p>
                {!worker && !readOnly && cert.view_route && (
                  <button type="button" onClick={() => viewSourceCertificate(cert)}
                    className="rounded border px-2 py-1 text-xs font-medium">View original certificate</button>
                )}
              </div>
              <p className="text-xs text-slate-600">{cert.provider || 'Provider pending review'} · {cert.verified_courses} verified of {cert.courses.length} linked courses</p>
              <ul className="mt-2 space-y-1 text-xs text-slate-700">
                {cert.courses.map((course, index) => (
                  <li key={course.record_id || index} className="flex flex-wrap justify-between gap-2">
                    <span>{course.title || 'Untitled course'}</span>
                    <span>{course.verified ? 'Verified' : 'Pending review'}</span>
                    {(course.scope_review_available || course.needs_scope_review) && (
                      <p className="w-full text-xs text-slate-600">
                        Safeguarding subjects: {scopeNames[course.safeguarding_scope] || 'Not specified'}
                        {!worker && course.scope_review_available && ' · Change coverage in Safeguarding certificate — Adults / Children above.'}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {certificateCount === 0 && <p className="text-sm text-slate-500">No current linked certificates. Historical certificates remain in the audit record.</p>}
          {(hub.history_certificates || []).length > 0 && (
            <div className="rounded-lg border p-3">
              <button type="button" onClick={() => setHistoryExpanded(!historyExpanded)}
                aria-expanded={historyExpanded} className="font-semibold text-xs text-slate-700">
                {historyExpanded ? 'Hide' : 'Show'} expired and superseded evidence ({hub.history_certificates.length} files)
              </button>
              {historyExpanded && (hub.history_certificates || []).map(cert => (
                <div key={cert.document_id} className="mt-2 text-xs text-slate-600">
                  <p className="font-medium">{cert.file_name}</p>
                  {cert.courses.map((course, index) => (
                    <p key={course.record_id || index}>{course.title || 'Training'} — {course.history_reason || 'historical'}</p>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!worker && !readOnly && queue && (
        <div className="border-t pt-3" data-testid="training-manager-queue">
          <p className="font-semibold text-sm">Manager work queue · {queueCount} open</p>
          <p className="text-xs text-slate-500">Pending certificate reviews remain in the existing evidence-review screen. Arranging a session does not verify training.</p>
          {!!hub.unmapped_evidence?.length && <p className="text-xs text-amber-800 mt-2">{hub.unmapped_evidence.length} unfamiliar course(s) retained for classification and review. Unknown training is never approved automatically.</p>}
          {employeeQueueRequests.slice(0, 8).map(req => (
            <div key={req.id} className="border-b py-3 space-y-2 text-xs">
              <p className="font-medium">{req.training_code?.replace(/_/g, ' ')} · {req.kind} · {req.status}</p>
              <div className="flex flex-wrap items-center gap-2">
                {req.status === 'requested' && <button type="button" disabled={mutating} onClick={() => updateRequest(req, 'acknowledged')} className="rounded border px-2 py-1">Acknowledge</button>}
                {req.status === 'enrolled' && <p className="text-xs text-slate-700">Enrolled with {req.provider_name}. Certificate still needed.</p>}
                {req.kind === 'training' && req.status !== 'enrolled' && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-slate-50 p-2">
                    <p className="w-full text-xs">After booking through the provider's business portal, record the real enrolment details here.</p>
                    <input aria-label="Training provider" placeholder="Training provider" className="rounded border p-2"
                      value={providerNames[req.id] || ''} onChange={e => setProviderNames(v => ({ ...v, [req.id]: e.target.value }))} />
                    <input aria-label="Enrolment reference" placeholder="Enrolment reference" className="rounded border p-2"
                      value={bookingReferences[req.id] || ''} onChange={e => setBookingReferences(v => ({ ...v, [req.id]: e.target.value }))} />
                    <button type="button" disabled={mutating || (providerNames[req.id] || '').trim().length < 2 || (bookingReferences[req.id] || '').trim().length < 3}
                      onClick={() => updateRequest(req, 'enrolled')}
                      className="rounded bg-slate-900 text-white px-3 py-2 disabled:opacity-50">Record enrolment</button>
                  </div>
                )}
                {req.status !== 'enrolled' && <label className="flex gap-1 items-center">Session time
                  <input aria-label="Session time" type="datetime-local" value={appointment[req.id] || ''} onChange={e => setAppointment(v => ({ ...v, [req.id]: e.target.value }))} className="rounded border p-1" />
                </label>}
                {req.status !== 'enrolled' && <input aria-label="Location or meeting link" placeholder="Location / Teams link" className="rounded border p-1" value={venue[req.id] || ''} onChange={e => setVenue(v => ({ ...v, [req.id]: e.target.value }))} />}
                {req.status !== 'enrolled' && <button type="button" disabled={mutating || !appointment[req.id] || !venue[req.id]} onClick={() => updateRequest(req, 'arranged')} className="rounded bg-teal-700 text-white px-2 py-1 disabled:opacity-50">Record appointment</button>}
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}
      {!worker && showManagerDetails && <p className="text-xs text-slate-500">Booking does not verify attendance, a certificate or competency.</p>}
    </section>
  );
}


// Worker pages authenticate with workerToken rather than the portal AuthProvider.
// Split the wrappers so worker induction (and its regression tests) never call
// useAuth outside the portal provider context.
function PortalTrainingHub(props) {
  const { token, user } = useAuth();
  const workspaceId = user?.workspace_id || user?.workspaceId || '';
  return <TrainingHubCore {...props} token={token} workspaceId={workspaceId} />;
}

export default function TrainingAuditHub(props) {
  if (props.mode === 'worker') {
    return <TrainingHubCore {...props} token={null} />;
  }
  return <PortalTrainingHub {...props} />;
}
